import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import { resetPassword, verifyPasswordResetToken } from '../lib/passwordResetApi';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setErrorMessage('パスワード再設定用のリンクが無効です。');
        setIsVerifying(false);
        return;
      }

      try {
        const res = await verifyPasswordResetToken(token);
        if (res.status === 200 && res.data?.valid) {
          setIsTokenValid(true);
        } else {
          setErrorMessage(
            res.message || 'パスワード再設定用のリンクが無効か、有効期限が切れています。'
          );
        }
      } catch {
        setErrorMessage('トークンの確認に失敗しました。時間をおいて再度お試しください。');
      } finally {
        setIsVerifying(false);
      }
    };

    verify();
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);

    if (password.length < 4) {
      setErrorMessage('パスワードは4文字以上で入力してください。');
      return;
    }

    if (password !== passwordConfirmation) {
      setErrorMessage('パスワード（確認）が一致しません。');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await resetPassword(token, password, passwordConfirmation);

      if (res.status === 200 && res.data?.reset) {
        setSuccessMessage(
          res.message || 'パスワードを再設定しました。新しいパスワードでログインしてください。'
        );
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      setErrorMessage(res.message || 'パスワードの再設定に失敗しました。');
    } catch {
      setErrorMessage('通信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-100 flex justify-center items-center min-h-screen px-4 py-8">
      <PageMeta title="新しいパスワードの設定" noIndex />
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl text-gray-800 font-semibold mb-4 text-center">新しいパスワードの設定</h2>

        {isVerifying ? (
          <p className="text-sm text-gray-600 text-center">リンクを確認しています...</p>
        ) : successMessage ? (
          <>
            <p className="text-sm text-green-700 leading-relaxed mb-6">{successMessage}</p>
            <Link
              to="/login"
              className="block text-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              ログイン画面へ
            </Link>
          </>
        ) : !isTokenValid ? (
          <>
            <p className="text-sm text-red-600 leading-relaxed mb-6">{errorMessage}</p>
            <Link
              to="/login/forgot-password"
              className="block text-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              パスワード再設定をやり直す
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              新しいパスワードを入力してください（4文字以上）。
            </p>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">
                  新しいパスワード
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  minLength={4}
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="passwordConfirmation"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  新しいパスワード（確認）
                </label>
                <input
                  type="password"
                  id="passwordConfirmation"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  minLength={4}
                  required
                />
              </div>
              {errorMessage && <div className="text-red-500 text-[14px] mb-2">{errorMessage}</div>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-500 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
              >
                {isSubmitting ? '設定中...' : 'パスワードを設定'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
